/**
 * The Entitlements component — a reusable, no-client-authority rendering of
 * `EntitlementService.entitlement_report()`.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C04C. `BILLING_UX_SPEC.md` §6,
 * required property "no client-side entitlement authority": this component
 * renders exactly what the server returned and computes nothing. There is no
 * branch below that decides whether a capability is available — every such
 * decision already happened in `auteur_cloud/entitlements/policy.py`, and the
 * one job left here is to show it honestly, including when it cannot be
 * shown at all.
 *
 * THE CONSTITUTIONAL FLOOR IS STRUCTURALLY UNCONDITIONAL
 *   `BILLING_UX_SPEC.md` §6: "A billing outage ... must visibly leave this
 *   section unaffected — the entitlement component's own layout is the proof
 *   point ... so the constitutional-floor block is never nested inside a card
 *   that a billing error could plausibly gray out or hide." `<ConstitutionalFloor
 *   />` below is therefore the FIRST element in this component's return, a
 *   sibling of (never a child of) the loading/error/report branches, and it
 *   renders in two modes:
 *     - the server's own `constitutional_capabilities` list, when a report
 *       was fetched — still true data, not a hardcoded list;
 *     - fixed reassurance copy, quoting the architectural guarantee rather
 *       than computing one, when the fetch itself failed outright and there
 *       is no server list to show. This is the one piece of static text in
 *       the component, and it exists because "the network call failed" must
 *       never be allowed to look like "your local capability is at risk".
 *
 * ENFORCEMENT HONESTY
 *   `enforced: bool` renders as a small badge distinguishing passive from
 *   enforced mode (§6) — this UI never implies enforcement is active when the
 *   document says otherwise, and it never implies a passive-mode "would
 *   refuse" is an actual refusal.
 *
 * PLAIN LANGUAGE (owner walkthrough F-32)
 *   Customers read this panel, so it speaks in plain words: "free features
 *   included with every account" rather than "constitutional floor", and a
 *   readable label for every lifecycle state and capability key. The server's
 *   own codes and detail sentences are still shown, but only inside a
 *   collapsed "Technical details" disclosure, so nothing honest is hidden
 *   and nothing internal is the headline.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import {
  BillingApiError,
  EntitlementReport,
  fetchEntitlementReport,
} from '../../api/billing';

/**
 * Display labels for `entitlements.CONSTITUTIONAL_CAPABILITIES`'s 14 keys.
 *
 * Presentation only: the *membership* of this list always comes from the
 * server (`report.constitutional_capabilities`); this map only turns a key
 * the server already told us about into readable text, and an unmapped key
 * (a future 15th capability) still renders — as its raw key — rather than
 * being silently dropped.
 */
const CONSTITUTIONAL_LABELS: Record<string, string> = {
  canon: 'Canon',
  memory: 'Memory',
  local_engines: 'Local generation engines',
  local_generation: 'Local generation',
  byo_providers: 'Bring-your-own provider keys',
  approval_gates: 'Human approval gates',
  export: 'Export',
  owned_files: 'Owned files',
  receipt_verify: 'Receipt verification',
  deletion: 'Deletion',
  diagnostics: 'Diagnostics',
  recovery: 'Recovery',
  honest_identity: 'Honest identity',
  redaction: 'Redaction',
};

/** Quotes the architectural guarantee; computes nothing. See file comment. */
const OUTAGE_REASSURANCE =
  'Canon, memory, local generation, export and the other free features are ' +
  'included with every account, whatever your plan or billing status. The list ' +
  'could not be loaded just now, but that never takes any of them away.';

/** Plain words for `subscription.lifecycle`. Unknown values still render. */
const LIFECYCLE_LABELS: Record<string, string> = {
  not_subscribed: 'No subscription',
  incomplete: 'Payment not completed',
  trialing: 'Free trial',
  active: 'Active',
  payment_failed: 'Payment issue',
  suspended: 'Suspended',
  cancel_pending: 'Cancelling at the end of the billing period',
  ended: 'Ended',
};

/**
 * A readable label for a capability key the server reported. Presentation
 * only: it never adds, removes or reorders keys. Known keys use their
 * curated labels; any other key is shown as words instead of a raw code.
 */
function capabilityLabel(key: string): string {
  if (CONSTITUTIONAL_LABELS[key]) return CONSTITUTIONAL_LABELS[key];
  const words = key.replace(/[._-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(ms));
}

function ConstitutionalFloor({ report }: { report: EntitlementReport | null }) {
  const keys = report ? report.constitutional_capabilities : null;
  return (
    <section className="entitlements-constitutional-floor" data-always-available="true">
      <h2>Free features included with every account</h2>
      {keys ? (
        <>
          <p>
            These {keys.length} features are always included, whatever your plan or billing
            status.
          </p>
          <ul className="entitlements-constitutional-list">
            {keys.map((key) => (
              <li key={key}>{capabilityLabel(key)}</li>
            ))}
          </ul>
        </>
      ) : (
        <p className="entitlements-constitutional-reassurance">{OUTAGE_REASSURANCE}</p>
      )}
    </section>
  );
}

function ModeBadge({ report }: { report: EntitlementReport }) {
  return (
    <p className="entitlements-mode-badge" data-mode={report.mode}>
      {report.enforced ? (
        <span>Plan limits are enforced: features outside your plan are not available.</span>
      ) : (
        <span>
          Plan limits are not enforced yet: nothing is blocked, and use outside your plan is only
          noted.
        </span>
      )}
    </p>
  );
}

function PlanSummary({ report }: { report: EntitlementReport }) {
  if (!report.plan) {
    return (
      <p className="entitlements-plan-missing" data-reason={report.reason_code}>
        You don't have a plan yet.
      </p>
    );
  }
  return (
    <dl className="entitlements-plan">
      <dt>Plan</dt>
      <dd>{report.plan.display_name}</dd>
      {report.grant ? (
        <>
          <dt>Since</dt>
          <dd>{formatDate(report.grant.granted_at) || report.grant.granted_at}</dd>
        </>
      ) : null}
      {report.plan.device_limit !== null ? (
        <>
          <dt>Device limit</dt>
          <dd>
            {report.devices.active.length} / {report.plan.device_limit}
          </dd>
        </>
      ) : null}
    </dl>
  );
}

function SubscriptionSummary({ report }: { report: EntitlementReport }) {
  const sub = report.subscription;
  // `entitlement_ends_at` is when paid access actually ends; it can be earlier
  // than `grace_ends_at` (the paid-through date wins), so never show the grace end.
  const accessEnds = sub.lifecycle === 'payment_failed' ? formatDate(sub.entitlement_ends_at) : null;
  return (
    <dl className="entitlements-subscription">
      <dt>Subscription</dt>
      <dd data-lifecycle={sub.lifecycle}>{LIFECYCLE_LABELS[sub.lifecycle] || sub.lifecycle}</dd>
      <dt>Paid service</dt>
      <dd>{sub.paid_service ? 'Available' : 'Not available'}</dd>
      {accessEnds ? (
        <>
          <dt>Access continues until</dt>
          <dd>{accessEnds}</dd>
        </>
      ) : null}
    </dl>
  );
}

/**
 * The server's own codes and sentences, kept for support conversations but
 * collapsed by default so they are never the headline (F-32).
 */
function TechnicalDetails({ report }: { report: EntitlementReport }) {
  const sub = report.subscription;
  return (
    <details className="entitlements-technical-details">
      <summary>Technical details</summary>
      <dl>
        <dt>Status code</dt>
        <dd>{report.reason_code || '—'}</dd>
        <dt>Subscription state</dt>
        <dd>{sub.lifecycle}</dd>
        {sub.detail ? (
          <>
            <dt>Detail</dt>
            <dd>{sub.detail}</dd>
          </>
        ) : null}
      </dl>
    </details>
  );
}

function DocumentSummary({ report }: { report: EntitlementReport }) {
  const doc = report.document;
  if (!doc) {
    // Nothing to show: an offline license is only issued with a paid plan.
    return null;
  }
  return (
    <dl className="entitlements-document">
      <dt>Offline license</dt>
      <dd>{doc.license_id}</dd>
      <dt>Issued</dt>
      <dd>{doc.issued_at}</dd>
      <dt>Expires</dt>
      <dd>{doc.expires_at || 'unbounded'}</dd>
      {doc.revoked ? (
        <>
          <dt>Revoked</dt>
          <dd>{doc.revoked_reason || 'no reason recorded'}</dd>
        </>
      ) : null}
    </dl>
  );
}

function CapabilityColumns({ report }: { report: EntitlementReport }) {
  return (
    <div className="entitlements-capability-columns">
      <div>
        <h3>Included in your plan</h3>
        <ul>
          {report.plan_capabilities.length ? (
            report.plan_capabilities.map((key) => <li key={key}>{capabilityLabel(key)}</li>)
          ) : (
            <li className="entitlements-empty">Nothing beyond the free features above</li>
          )}
        </ul>
      </div>
      {report.effective_capabilities.length ? (
        <div>
          <h3>Available to you right now</h3>
          <ul>
            {report.effective_capabilities.map((key) => (
              <li key={key}>{capabilityLabel(key)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** The report-dependent part of the panel; exported for rendering tests. */
export function EntitlementReportDetails({ report }: { report: EntitlementReport }): React.ReactElement {
  return (
    <>
      <ModeBadge report={report} />
      <PlanSummary report={report} />
      <SubscriptionSummary report={report} />
      <DocumentSummary report={report} />
      <CapabilityColumns report={report} />
      <TechnicalDetails report={report} />
    </>
  );
}

export interface EntitlementsPanelProps {
  /** Called after every successful (re)fetch, so an embedding page (e.g. Billing) can react. */
  onReport?: (report: EntitlementReport) => void;
}

export function EntitlementsPanel({ onReport }: EntitlementsPanelProps = {}): React.ReactElement {
  const [report, setReport] = useState<EntitlementReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchEntitlementReport();
      setReport(next);
      setUnavailable(false);
      setError('');
      if (onReport) onReport(next);
    } catch (failure) {
      setReport(null);
      setUnavailable(true);
      setError(
        failure instanceof BillingApiError
          ? failure.message
          : 'the entitlement service could not be reached',
      );
    } finally {
      setLoading(false);
    }
  }, [onReport]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <section className="entitlements-panel">
      {/* Sibling of, never nested inside, the branches below — see file comment. */}
      <ConstitutionalFloor report={report} />

      {loading && !report ? <p className="entitlements-loading">Loading your plan…</p> : null}

      {unavailable ? (
        <div className="entitlements-unavailable" role="alert" data-state="Unavailable">
          <p>Your plan details are unavailable right now: {error}</p>
          <button type="button" onClick={refresh}>
            Retry
          </button>
        </div>
      ) : null}

      {report && !report.store_available ? (
        <p className="entitlements-store-outage" role="status" data-state="Unavailable">
          Your plan details could not be read right now. This does not block anything, and your
          free features and local work are unaffected.
        </p>
      ) : null}

      {report ? <EntitlementReportDetails report={report} /> : null}
    </section>
  );
}

export default EntitlementsPanel;
